'use client';

import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';

interface EmojiRatingProps {
  rating: number;
  onChange?: (rating: number) => void;
  readonly?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const EMOJI_OPTIONS = [
  { value: 1, emoji: '😞' },
  { value: 2, emoji: '😕' },
  { value: 3, emoji: '😐' },
  { value: 4, emoji: '😊' },
  { value: 5, emoji: '😍' },
];

const sizes = {
  sm: { emoji: 'text-2xl', container: 'gap-3' },
  md: { emoji: 'text-3xl', container: 'gap-4' },
  lg: { emoji: 'text-4xl', container: 'gap-5' },
};

export default function EmojiRating({
  rating,
  onChange,
  readonly = false,
  size = 'lg',
  className,
}: EmojiRatingProps) {
  const t = useTranslations('reviews');

  return (
    <div className={cn('flex items-end justify-center', sizes[size].container, className)}>
      {EMOJI_OPTIONS.map((option) => {
        const isSelected = option.value === rating;
        const hasSelection = rating > 0;

        return (
          <button
            key={option.value}
            type="button"
            disabled={readonly}
            onClick={() => onChange?.(option.value)}
            className={cn(
              'flex flex-col items-center transition-all duration-200',
              !readonly && 'cursor-pointer',
              readonly && 'cursor-default',
            )}
            aria-label={t('starLabel', { star: option.value })}
          >
            <span
              className={cn(
                sizes[size].emoji,
                'transition-all duration-200',
                isSelected && 'scale-125',
                hasSelection && !isSelected && 'grayscale opacity-40',
                !hasSelection && 'grayscale-[50%] opacity-70',
                !readonly && 'hover:scale-110 hover:grayscale-0 hover:opacity-100',
              )}
            >
              {option.emoji}
            </span>
            <span
              className={cn(
                'text-xs mt-1.5 font-medium transition-colors',
                isSelected ? 'text-orange-600' : 'text-gray-400',
                size === 'sm' && 'text-[10px]',
              )}
            >
              {t(`rating${option.value}`)}
            </span>
          </button>
        );
      })}
    </div>
  );
}
